import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _asyncToGenerator from '@babel/runtime/helpers/asyncToGenerator';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _regeneratorRuntime from '@babel/runtime/regenerator';
import React, { useRef, useState, useEffect } from 'react';
import { bool, string, shape, func } from 'prop-types';
import classnames from 'classnames';
import { iconUser, iconCompany, iconVehicleModelX, iconContract, iconLyrics } from '@tesla/design-system-icons';
import { Heading, Icon, Text } from '@tesla/design-system-react';
import BackLink from '../Common/BackLink/index.js';
import ImageCompositor from '../Common/ImageCompositor/index.js';
import useStickyHeader from '../Hooks/useStickyHeader.js';
import useIsSmallScreen from '../Hooks/useIsSmallScreen.js';
import buildC360AssetHeaderDetails from './Helpers/Utilities.js';
import styles from './VehicleHeader.module.css.js';
import transformAssetResults, { buildOptionCodes } from './Helpers/Transform.js';

var _excluded = ["height"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var PERSON = 'PERSON';
var BUSINESS = 'BUSINESS';
var ACCOUNT_TYPE = {
  PERSON: PERSON,
  BUSINESS: BUSINESS
};
var MODEL_MAPPER = {
  m3: 'Model 3',
  ct: 'Cybertruck',
  ms: 'Model S',
  mt: 'Roadster',
  my: 'Model Y',
  mx: 'Model X',
  rd: 'Roadster',
  ts: 'Semi'
};
var c360SearchUrl = '/account/api/search/C360Search';
var VehicleHeader = function VehicleHeader(_ref) {
  var _ref$showBack = _ref.showBack,
    showBack = _ref$showBack === void 0 ? true : _ref$showBack,
    aosUrl = _ref.aosUrl,
    adfsToken = _ref.adfsToken,
    vin = _ref.vin,
    vmap = _ref.vmap,
    assetBaseUrl = _ref.assetBaseUrl,
    t = _ref.t,
    _ref$onLoadingEnd = _ref.onLoadingEnd,
    onLoadingEnd = _ref$onLoadingEnd === void 0 ? function () {} : _ref$onLoadingEnd,
    className = _ref.className,
    customStyle = _ref.customStyle,
    C360SearchTestConsumerApp = _ref.C360SearchTestConsumerApp;
  var isSticky = useStickyHeader();
  var _useIsSmallScreen = useIsSmallScreen(),
    _useIsSmallScreen2 = _slicedToArray(_useIsSmallScreen, 1),
    isSmallScreen = _useIsSmallScreen2[0];
  var node = useRef();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    rect = _useState2[0],
    setRect = _useState2[1];
  var _useState3 = useState({}),
    _useState4 = _slicedToArray(_useState3, 2),
    assetResults = _useState4[0],
    setAssetResults = _useState4[1];
  var _useState5 = useState(),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  var transformResult = function transformResult(resp) {
    var _resp$data, _resp$data$data;
    var assetList = (resp === null || resp === void 0 ? void 0 : (_resp$data = resp.data) === null || _resp$data === void 0 ? void 0 : (_resp$data$data = _resp$data.data) === null || _resp$data$data === void 0 ? void 0 : _resp$data$data.assetDetails) || [];
    var activeAsset = assetList.find(function (asset) {
      return (asset === null || asset === void 0 ? void 0 : asset.is_active) === true;
    }) || {};
    setAssetResults(transformAssetResults(activeAsset));
  };
  var fetchAssetDetails = function fetchAssetDetails() {
    var requestOptions = {
      body: JSON.stringify({
        query: buildC360AssetHeaderDetails(vin)
      }),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: adfsToken
      },
      method: 'POST',
      credentials: 'omit'
    };
    if (C360SearchTestConsumerApp) {
      requestOptions.headers.C360_SEARCH_TEST_CONSUMER_APP = C360SearchTestConsumerApp;
    }
    var endpoint = aosUrl + c360SearchUrl;
    setLoading(true);
    fetch(endpoint, requestOptions).then(/*#__PURE__*/function () {
      var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(response) {
        var result;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (!(response.status === 200)) {
                _context.next = 5;
                break;
              }
              _context.next = 3;
              return response === null || response === void 0 ? void 0 : response.json();
            case 3:
              result = _context.sent;
              transformResult(result);
            case 5:
            case "end":
              return _context.stop();
          }
        }, _callee);
      }));
      return function (_x) {
        return _ref2.apply(this, arguments);
      };
    }())["catch"](function () {})["finally"](function () {
      setLoading(false);
      onLoadingEnd();
    });
  };
  var ImageSideBar = /*#__PURE__*/React.createElement("aside", {
    className: "".concat(styles['tw-react--vehicle-header--car-image'], " tds-flex-item")
  }, /*#__PURE__*/React.createElement(ImageCompositor, {
    optionCodes: buildOptionCodes(assetResults === null || assetResults === void 0 ? void 0 : assetResults.mktOptions),
    mfgCodes: assetResults === null || assetResults === void 0 ? void 0 : assetResults.mfgOptions,
    model: assetResults === null || assetResults === void 0 ? void 0 : assetResults.modelCode,
    isStackedImage: false,
    productType: "VEHICLE",
    width: 160,
    height: 90,
    baseUrl: assetBaseUrl
  }));
  useEffect(function () {
    var debounce;
    var observer = new ResizeObserver(function (entries) {
      window.cancelAnimationFrame(debounce);
      debounce = window.requestAnimationFrame(function () {
        entries.forEach(function (entry) {
          var _entry$target$getBoun = entry.target.getBoundingClientRect(),
            height = _entry$target$getBoun.height,
            rest = _objectWithoutProperties(_entry$target$getBoun, _excluded);
          if ((rect === null || rect === void 0 ? void 0 : rect.height) === height) {
            return;
          }
          setRect(_objectSpread(_objectSpread({}, rest), {}, {
            height: height
          }));
        });
      });
    });
    if (node.current) {
      observer.observe(node.current);
    }
    return function () {
      window.cancelAnimationFrame(debounce);
      observer.disconnect();
    };
  });
  useEffect(function () {
    fetchAssetDetails();
  }, []);
  var Header = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("style", null, ":root {\n        --aos--components--account-details--account-info-header--block-size:\n          ".concat((rect === null || rect === void 0 ? void 0 : rect.height) || 0, "px;\n      }")), /*#__PURE__*/React.createElement("header", {
    className: classnames('tds-sticky--top', styles['tw-react--aos--components--vehicle--header'], styles['tw-react--sticky-top--40px'], className, _defineProperty(_defineProperty(_defineProperty(_defineProperty({}, styles['tw-react--vehicle-header-margin'], !isSticky), styles['tw-react--vehicle-header-sticky-custom-margin'], isSticky && customStyle), styles['tw-react--sticky-bg-shadow'], isSticky), 'tds--horizontal_padding--large', isSmallScreen)),
    ref: node
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex', 'tds-flex--nowrap', {
      'tds-flex--center': !isSmallScreen,
      'tds-flex--top': isSmallScreen,
      'tds-flex--as_columns': isSmallScreen
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex-item', {
      'tds-flex': !isSmallScreen && isSticky,
      'tds-flex--as-rows': !isSmallScreen && isSticky
    })
  }, /*#__PURE__*/React.createElement(Heading, {
    is: isSticky ? 'h5' : 'h1',
    weight: "medium",
    className: classnames(_defineProperty(_defineProperty(_defineProperty({}, styles['tw-react--vehicle-header-heading--unsticky'], !isSticky), styles['tw-react--vehicle-header-heading--sticky'], isSticky), 'tds--no_padding', isSticky || showBack))
  }, (assetResults === null || assetResults === void 0 ? void 0 : assetResults.year) || '-', " ", MODEL_MAPPER[assetResults === null || assetResults === void 0 ? void 0 : assetResults.modelCode] || '-'), /*#__PURE__*/React.createElement("div", {
    className: classnames('tds-flex', 'tds-flex--gutters')
  }, (assetResults === null || assetResults === void 0 ? void 0 : assetResults.fullName) && (assetResults === null || assetResults === void 0 ? void 0 : assetResults.accountType) && /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--vehicle-header-subheading'])
  }, (assetResults === null || assetResults === void 0 ? void 0 : assetResults.accountType) === ACCOUNT_TYPE.PERSON ? /*#__PURE__*/React.createElement(Icon, {
    data: iconUser,
    inline: true,
    align: "text-middle",
    className: "tds-icon--small",
    size: "small"
  }) : /*#__PURE__*/React.createElement(Icon, {
    data: iconCompany,
    inline: true,
    align: "text-middle",
    className: "tds-icon--small",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--vehicle-text-small']
  }, assetResults === null || assetResults === void 0 ? void 0 : assetResults.fullName)), /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--vehicle-header-subheading'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconVehicleModelX,
    inline: true,
    align: "text-middle",
    className: "tds-icon--small",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--vehicle-text-small']
  }, "VIN ", assetResults === null || assetResults === void 0 ? void 0 : assetResults.vin))), /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--vehicle-header-subheading'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconContract,
    inline: true,
    align: "text-middle",
    className: "tds-icon--small",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--vehicle-text-small']
  }, assetResults === null || assetResults === void 0 ? void 0 : assetResults.referenceNumber))), vmap ? /*#__PURE__*/React.createElement(Heading, {
    is: "h5",
    weight: "regular",
    className: classnames('tds--no_padding', 'display-flex--center', styles['tw-react--vehicle-header-subheading'])
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    data: iconLyrics,
    inline: true,
    align: "text-middle",
    className: "tds-icon--small",
    size: "small"
  }), /*#__PURE__*/React.createElement(Text, {
    is: "span",
    className: styles['tw-react--vehicle-text-small']
  }, "VMAP ", vmap))) : null)))));
  var backLink = !isSticky ? /*#__PURE__*/React.createElement(BackLink, {
    t: t,
    show: showBack
  }) : null;
  if (loading) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, backLink, !isSticky ? /*#__PURE__*/React.createElement("div", {
    className: "tds-flex"
  }, ImageSideBar, Header) : Header);
};
VehicleHeader.propTypes = {
  showBack: bool.isRequired,
  vin: string.isRequired,
  vmap: string,
  aosUrl: string.isRequired,
  adfsToken: string.isRequired,
  assetBaseUrl: string.isRequired,
  t: shape({
    back: string
  }),
  onLoadingEnd: func,
  className: string,
  customStyle: bool,
  C360SearchTestConsumerApp: string
};
VehicleHeader.defaultProps = {
  t: {
    back: 'Back'
  },
  onLoadingEnd: function onLoadingEnd() {},
  className: '',
  customStyle: true,
  vmap: '',
  C360SearchTestConsumerApp: ''
};

export { VehicleHeader as default };
